import type { ProductCard } from "./tools";

/**
 * How a turn's tool results become product cards, shared by the hosted chat (browser) and the
 * WhatsApp renderer's callers. Type-only imports: the browser bundle loads this file and must
 * never pull in the server.
 */

/** A product to put in the basket and how many of it: a recipe line's packs. */
export interface BasketPick {
	product: ProductCard;
	quantity: number;
}

export interface RecipeOutput {
	matches: Array<{
		ingredient: string;
		best: ProductCard | null;
		/** Packs of `best` the line needs; older turns did not say (one). */
		quantity?: number;
		alternatives: ProductCard[];
	}>;
	/** Refused: it named what the store never sells; the model writes it again. */
	rejected?: { restricted: string[] } | null;
}

/**
 * A tool result's products as rows: one per product a list search named ("rice and water"
 * is a row of rice, then a row of water), else one row. Rows that found nothing are left
 * out: the reply says so.
 */
export function productRowsFrom(output: unknown): ProductCard[][] {
	const products = productsFrom(output);
	const searches = (output as { searches?: Array<{ productIds: string[] }> })
		?.searches;
	if (!searches || searches.length < 2) {
		return products.length ? [products] : [];
	}
	const byId = new Map(products.map((p) => [p.id, p]));
	return searches
		.map((search) =>
			search.productIds.flatMap((id) => {
				const card = byId.get(id);
				return card ? [card] : [];
			}),
		)
		.filter((row) => row.length > 0);
}

/** The products a tool result shows: a search's list, a recipe's best matches. */
export function productsFrom(output: unknown): ProductCard[] {
	const value = output as {
		products?: ProductCard[];
		matches?: Array<{ best: ProductCard | null }>;
	};
	if (value?.products) {
		return value.products;
	}
	if (value?.matches) {
		return value.matches.flatMap((m) => (m.best ? [m.best] : []));
	}
	return [];
}
